import React from 'react';
import { useImageViewer } from './ImageViewerProvider';

interface YapImageGridProps {
  images: string[];
  className?: string;
  /**
   * When true (e.g. on the yap detail screen), a single image is shown in full
   * at its natural aspect ratio instead of being cropped to the compact tile.
   */
  detail?: boolean;
}

/**
 * Compact, Twitter/X-style image attachments for yaps.
 *
 * The whole grid is ONE rounded, overflow-clipped rectangle so no image can
 * ever render past the card boundary (issue #1). Inner cells are separated by a
 * thin 4px gap and each one object-covers its cell; only the grid's outer
 * corners are rounded — inner edges are square (the wrapper clips them).
 *
 * Tapping a tile opens the app-level fullscreen viewer (see ImageViewerProvider)
 * with the WHOLE post's image array + the tapped index, so the viewer stays open
 * across feed re-renders and supports prev/next navigation.
 *
 * Layouts (scaled down for compactness):
 *   1 → full width, 220px tall
 *   2 → 50/50, 180px tall
 *   3 → left 55% full-height + 2 stacked right (~98px each), 200px total
 *   4 → 2×2, 130px cells
 *   5 → 2 on top (130px) + 3 on bottom (98px)
 *   6 → 3×2, 98px cells
 */

const RADIUS = 'rounded-xl';
const GAP = 'gap-1'; // 4px between cells

export function YapImageGrid({ images, className = '', detail = false }: YapImageGridProps) {
  const { open } = useImageViewer();
  const imgs = (images || []).filter(Boolean).slice(0, 6);
  if (imgs.length === 0) return null;

  // A single cell: fills its grid cell, crops with object-cover, opens the
  // shared viewer at this tile's index on tap.
  const Tile: React.FC<{ src: string; alt: string; index: number; style?: React.CSSProperties }> = ({ src, alt, index, style }) => (
    <div className="w-full h-full overflow-hidden bg-black/5 dark:bg-white/5" style={style}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onClick={(e) => { e.stopPropagation(); open(imgs, index); }}
        className="w-full h-full cursor-zoom-in"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );

  // Outer wrapper: rounded + overflow-hidden so children can't poke past it.
  const wrapper = `mt-1 overflow-hidden ${RADIUS} border border-black/5 dark:border-white/10 ${className}`;

  // 1 image
  if (imgs.length === 1) {
    // Detail screen: show the whole photo at its natural aspect (no crop).
    if (detail) {
      return (
        <div className={wrapper}>
          <img
            src={imgs[0]}
            alt="Yap image 1"
            onClick={(e) => { e.stopPropagation(); open(imgs, 0); }}
            className="w-full h-auto cursor-zoom-in"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </div>
      );
    }
    return (
      <div className={wrapper} style={{ height: 220 }}>
        <Tile src={imgs[0]} alt="Yap image 1" index={0} />
      </div>
    );
  }

  // 2 images — side by side, 180px tall
  if (imgs.length === 2) {
    return (
      <div className={`${wrapper} grid grid-cols-2 ${GAP}`} style={{ height: 180 }}>
        {imgs.map((img, i) => (
          <Tile key={i} src={img} alt={`Yap image ${i + 1}`} index={i} />
        ))}
      </div>
    );
  }

  // 3 images — left 55% full-height + 2 stacked right (~98px each)
  if (imgs.length === 3) {
    return (
      <div
        className={`${wrapper} grid ${GAP}`}
        style={{ gridTemplateColumns: '55fr 45fr', gridTemplateRows: '1fr 1fr', height: 200 }}
      >
        <Tile src={imgs[0]} alt="Yap image 1" index={0} style={{ gridRow: '1 / 3' }} />
        <Tile src={imgs[1]} alt="Yap image 2" index={1} />
        <Tile src={imgs[2]} alt="Yap image 3" index={2} />
      </div>
    );
  }

  // 4 images — 2×2, 130px cells
  if (imgs.length === 4) {
    return (
      <div className={`${wrapper} grid grid-cols-2 ${GAP}`} style={{ gridAutoRows: '130px' }}>
        {imgs.map((img, i) => (
          <Tile key={i} src={img} alt={`Yap image ${i + 1}`} index={i} />
        ))}
      </div>
    );
  }

  // 5 images — 2 on top (130px), 3 on bottom (98px)
  if (imgs.length === 5) {
    return (
      <div
        className={`${wrapper} grid ${GAP}`}
        style={{ gridTemplateColumns: 'repeat(6, 1fr)', gridTemplateRows: '130px 98px' }}
      >
        {imgs.map((img, i) => (
          <Tile key={i} src={img} alt={`Yap image ${i + 1}`} index={i} style={{ gridColumn: i < 2 ? 'span 3' : 'span 2' }} />
        ))}
      </div>
    );
  }

  // 6 images — 3×2, 98px cells
  return (
    <div className={`${wrapper} grid grid-cols-3 ${GAP}`} style={{ gridAutoRows: '98px' }}>
      {imgs.map((img, i) => (
        <Tile key={i} src={img} alt={`Yap image ${i + 1}`} index={i} />
      ))}
    </div>
  );
}
